import { Mediator } from '@spotahome/soyuz-mediator';
import { trans } from '@spotahome/soyuz/client';
import {
  BILLS_INCLUDED_VALUES,
  CHANNELS,
  isPlusPlan
} from '@spotahome/ui-library';
import openDepositProtectionModal from '@spotahome/ui-library/src/tenant/components/DepositProtectionModal';
import openCheckPlace24hModal from '@spotahome/ui-library/src/tenant/components/CheckPlace24hModal';
import openLastMinuteBackupModal from '@spotahome/ui-library/src/tenant/components/LastMinuteBackupModal';
import openInstantBookingModal from '@spotahome/ui-library/src/tenant/components/InstantBookingModal';
import openLandlordVerifiedModal from '@spotahome/ui-library/src/tenant/components/LandlordVerifiedModal';
import openPropertyVerifiedModal from '@spotahome/ui-library/src/tenant/components/PropertyVerifiedModal';
import openPlusValuePropositionModal from '@spotahome/ui-library/src/tenant/components/PlusValueProposition/PlusValuePropositionModal';
import { getBillsIncluded as getBillsIncludedUtil } from '@spotahome/ui-library/src/utils/listing';

const VALUE_PROPOSITION_ICONS = {
  depositProtection: 'lock_closed',
  noDeposit: 'savings',
  isChecked: 'check_circle_transparent',
  instantBooking: 'electricity_bill',
  billsIncluded: 'savings',
  someBillsIncluded: 'savings',
  longTerm: 'calendar_month',
  landlordType: 'key_locker',
  lastMinuteBackUp: 'support',
  gracePeriodCancellation: 'history',
  check24Hours: 'mystery',
  landlordVerified: 'verified',
  plus: 'spoke'
};

const handleGracePeriodCancellationClick = () => {
  Mediator.publish(CHANNELS.OPEN_GRACE_PERIOD_CANCELLATION_MODAL);
};

const handleInformationModalClick = ({ title, body }) => {
  Mediator.publish(CHANNELS.OPEN_INFORMATION_MODAL, {
    title,
    body,
    fullscreenOnMobile: false
  });
};

const BILL_ID_TO_TRANS_KEY = {
  5000: 'search.filter.electricity',
  5001: 'search.filter.water',
  5002: 'search.filter.gas',
  5003: 'search.filter.wifi'
};

const getIncludedBillsSubtext = (bills = {}) => {
  const includedLabels = Object.entries(bills)
    .filter(([, value]) => value === true)
    .map(([id]) => trans(BILL_ID_TO_TRANS_KEY[Number(id)]))
    .filter(Boolean);
  return includedLabels.length > 0 ? includedLabels.join(', ') : '';
};

/**
 * Builds chip list for ListingChipsContainer from the same data as ValuePropositionFeatures.
 * All possible chips are included; each has an `include` boolean for later filtering.
 * Elements are in the desired display order.
 * @param {Object} params
 * @param {Object} params.listing - listing object (instantBooking, checked, bills, allowsPermanentContract, isBestChoice, plan)
 * @param {boolean} params.hasNoDeposit - from usePricingContext
 * @param {Object} params.landlordInformation - from useLandlordInformation (optional, { type } for landlord type)
 * @returns {Array<{ name: string, icon: string, value: boolean, include: boolean }>}
 */
export const getValuePropositionChips = ({
  listing,
  hasNoDeposit = false,
  landlordInformation = null,
  homechecker = null,
  verificationDate = null
}) => {
  const {
    instantBooking = {},
    checked = false,
    bills = {},
    allowsPermanentContract = false,
    landlordScoring = null
  } = listing;
  const {
    isEnabled: instantBookingEnabled = false,
    rules: instantBookingRules = []
  } = instantBooking;
  const billsIncluded = getBillsIncludedUtil(bills);
  const isBillsIncluded = billsIncluded === BILLS_INCLUDED_VALUES.all;
  const isSomeBillsIncluded = billsIncluded === BILLS_INCLUDED_VALUES.some;
  const showDepositProtection = !hasNoDeposit;
  const isPlus = isPlusPlan(listing.plan);

  const chips = [
    {
      name: trans('listing.labels.noDeposit.title'),
      icon: VALUE_PROPOSITION_ICONS.noDeposit,
      value: true,
      include: hasNoDeposit,
      onClick: () =>
        handleInformationModalClick({
          title: trans('listing.labels.noDeposit.title'),
          body: trans('listing.labels.noDeposit.description')
        })
    },
    {
      name: trans('listing.labels.bills.all.title'),
      icon: VALUE_PROPOSITION_ICONS.billsIncluded,
      value: true,
      include: isBillsIncluded,
      onClick: () =>
        handleInformationModalClick({
          title: trans('listing.labels.bills.all.title'),
          body: trans('listing.labels.bills.description')
        })
    },
    {
      name: trans('listing.labels.bills.some.title'),
      icon: VALUE_PROPOSITION_ICONS.someBillsIncluded,
      value: true,
      include: isSomeBillsIncluded,
      subtext: isSomeBillsIncluded ? getIncludedBillsSubtext(bills) : undefined,
      onClick: () =>
        handleInformationModalClick({
          title: trans('listing.labels.bills.some.title'),
          body: trans('listing.labels.bills.description')
        })
    },
    {
      name: trans('property_show.book_now_card.instant_booking'),
      icon: VALUE_PROPOSITION_ICONS.instantBooking,
      value: true,
      include: instantBookingEnabled,
      onClick: () => openInstantBookingModal({ rules: instantBookingRules })
    },
    {
      name: trans('homecard.label.long-term'),
      icon: VALUE_PROPOSITION_ICONS.longTerm,
      value: true,
      include: allowsPermanentContract,
      onClick: () =>
        handleInformationModalClick({
          title: trans('homecard.label.long-term'),
          body: () => (
            <>
              <p>{trans('listing.labels.long.term.description')}</p>
              <p>{trans('search.booknow-card-label.tooltip.permanent.es')}</p>
            </>
          )
        })
    },
    {
      name: trans('homecard.label.deposit.protected'),
      icon: VALUE_PROPOSITION_ICONS.depositProtection,
      value: true,
      include: showDepositProtection,
      onClick: openDepositProtectionModal
    },
    {
      name: trans('property_show.book_now_card.checked'),
      icon: VALUE_PROPOSITION_ICONS.isChecked,
      value: true,
      include: checked,
      onClick: () =>
        openPropertyVerifiedModal({ homechecker, verificationDate })
    },
    {
      name: trans(
        'property_show.book_now_card.grace.period.cancellation.title'
      ),
      icon: VALUE_PROPOSITION_ICONS.gracePeriodCancellation,
      value: true,
      include: false,
      isGuarantee: true,
      onClick: handleGracePeriodCancellationClick
    },
    {
      name: trans('listing.landlord_info.verified_landlord'),
      icon: VALUE_PROPOSITION_ICONS.landlordVerified,
      value: true,
      include: true,
      isGuarantee: true,
      onClick: () =>
        openLandlordVerifiedModal({ landlordInformation, landlordScoring })
    },
    {
      name: trans('property_show.book_now_card.24h'),
      icon: VALUE_PROPOSITION_ICONS.check24Hours,
      value: true,
      include: true,
      isGuarantee: true,
      onClick: openCheckPlace24hModal
    },
    {
      name: trans('spotahome.guarantee.last.minute.backup'),
      icon: VALUE_PROPOSITION_ICONS.lastMinuteBackUp,
      value: true,
      include: true,
      isGuarantee: true,
      onClick: openLastMinuteBackupModal
    },
    {
      name: 'Spotahome Plus',
      icon: VALUE_PROPOSITION_ICONS.plus,
      value: true,
      include: isPlus,
      onClick: openPlusValuePropositionModal
    }
  ];

  return chips.filter(chip => chip.include);
};
